import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import useLocale from 'next-translate/useTranslation';

import clsxm from '@/lib/clsxm';
import type { DataType } from '@/components/workflows/nodes/BaseNode';
import type { HandleDataType } from '@/lib/workflows/store';
import type {
  CompatibleNodeOption,
  HandleDirection,
} from '@/lib/workflows/nodeCompatibility';

const DATA_TYPE_ACCENT: Record<DataType, { dot: string; text: string }> = {
  text: {
    dot: 'bg-blue-500',
    text: 'text-blue-600 dark:text-blue-400',
  },
  image: {
    dot: 'bg-purple-500',
    text: 'text-purple-600 dark:text-purple-400',
  },
  video: {
    dot: 'bg-cyan-500',
    text: 'text-cyan-600 dark:text-cyan-400',
  },
  audio: {
    dot: 'bg-indigo-500',
    text: 'text-indigo-600 dark:text-indigo-400',
  },
};

const UNKNOWN_ACCENT = {
  dot: 'bg-gray-400',
  text: 'text-gray-500 dark:text-gray-400',
};

function accentFor(type: HandleDataType) {
  if (type === 'unknown') return UNKNOWN_ACCENT;
  return DATA_TYPE_ACCENT[type];
}

interface ConnectionEndMenuProps {
  // Screen-space position of the drop point
  position: { x: number; y: number };
  dataType: HandleDataType;
  direction: HandleDirection;
  options: CompatibleNodeOption[];
  onSelect: (option: CompatibleNodeOption) => void;
  onClose: () => void;
}

const MENU_WIDTH = 280;
const VIEWPORT_MARGIN = 12;

export function ConnectionEndMenu({
  position,
  dataType,
  direction,
  options,
  onSelect,
  onClose,
}: ConnectionEndMenuProps) {
  const { t } = useLocale('common');
  const menuRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [adjustedPos, setAdjustedPos] = useState(position);

  // Title shown at the top of the menu. Built without t() so the dynamic
  // {type} placeholder is filled in (next-translate doesn't interpolate
  // fallback keys when no translation entry exists).
  const typeLabel = dataType === 'unknown' ? '' : dataType;
  const headline =
    direction === 'source'
      ? typeLabel
        ? `Add ${typeLabel} node`
        : 'Add node'
      : typeLabel
        ? `Add ${typeLabel} source`
        : 'Add source';

  const filteredOptions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (opt) =>
        opt.defaultLabel.toLowerCase().includes(q) ||
        opt.defaultDescription.toLowerCase().includes(q) ||
        opt.type.toLowerCase().includes(q),
    );
  }, [options, query]);

  // Reset highlight when filter changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  // Clamp menu position so it stays in viewport
  useLayoutEffect(() => {
    const node = menuRef.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    let x = position.x;
    let y = position.y;

    if (x + rect.width > window.innerWidth - VIEWPORT_MARGIN) {
      x = Math.max(
        VIEWPORT_MARGIN,
        window.innerWidth - rect.width - VIEWPORT_MARGIN,
      );
    }
    if (y + rect.height > window.innerHeight - VIEWPORT_MARGIN) {
      y = Math.max(
        VIEWPORT_MARGIN,
        window.innerHeight - rect.height - VIEWPORT_MARGIN,
      );
    }
    if (x !== adjustedPos.x || y !== adjustedPos.y) {
      setAdjustedPos({ x, y });
    }
  }, [position, adjustedPos.x, adjustedPos.y, filteredOptions.length]);

  // Auto-focus the search input
  useEffect(() => {
    searchRef.current?.focus();
  }, []);

  // Scroll selected option into view when navigating
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const item = list.querySelector<HTMLButtonElement>(
      `[data-option-index="${selectedIndex}"]`,
    );
    if (item) {
      item.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex]);

  const commit = useCallback(
    (option: CompatibleNodeOption | undefined) => {
      if (!option) return;
      onSelect(option);
    },
    [onSelect],
  );

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((idx) =>
          filteredOptions.length === 0 ? 0 : (idx + 1) % filteredOptions.length,
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((idx) =>
          filteredOptions.length === 0
            ? 0
            : (idx - 1 + filteredOptions.length) % filteredOptions.length,
        );
      } else if (e.key === 'Enter') {
        e.preventDefault();
        commit(filteredOptions[selectedIndex]);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [filteredOptions, selectedIndex, commit, onClose]);

  // Click outside to close
  useEffect(() => {
    const handlePointerDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    // Defer one tick so the click that opened the menu doesn't immediately close it
    const timer = window.setTimeout(() => {
      document.addEventListener('mousedown', handlePointerDown);
    }, 0);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [onClose]);

  const accent = accentFor(dataType);

  return (
    <div
      ref={menuRef}
      role='menu'
      aria-label={headline}
      style={{
        position: 'fixed',
        left: adjustedPos.x,
        top: adjustedPos.y,
        width: MENU_WIDTH,
        zIndex: 2000,
      }}
      className='workflow-connection-menu-anim bg-white dark:bg-gray-800 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden'
    >
      {/* Header */}
      <div className='flex items-center gap-2 px-3 py-2 border-b border-gray-200 dark:border-gray-700'>
        <span
          className={clsxm('w-2 h-2 rounded-full flex-shrink-0', accent.dot)}
        />
        <span
          className={clsxm(
            'text-[11px] font-semibold uppercase tracking-wide',
            accent.text,
          )}
        >
          {headline}
        </span>
      </div>

      {/* Search */}
      <div className='px-2 pt-2'>
        <input
          ref={searchRef}
          type='text'
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('Search nodes...')}
          className='w-full px-2.5 py-1.5 text-sm rounded-md bg-gray-100 dark:bg-gray-900 border border-transparent focus:border-primary-500 focus:bg-white dark:focus:bg-gray-800 outline-none text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500'
        />
      </div>

      {/* Options */}
      <div ref={listRef} className='py-1 px-1 max-h-72 overflow-y-auto'>
        {filteredOptions.length === 0 ? (
          <div className='px-3 py-6 text-center text-xs text-gray-500 dark:text-gray-400'>
            {t('No matching nodes')}
          </div>
        ) : (
          filteredOptions.map((option, index) => (
            <button
              key={option.type}
              data-option-index={index}
              onClick={() => commit(option)}
              onMouseEnter={() => setSelectedIndex(index)}
              className={clsxm(
                'w-full text-left rounded-md px-2 py-1.5 flex items-start gap-2 transition-colors',
                index === selectedIndex
                  ? 'bg-primary-50 dark:bg-primary-900/30 text-gray-900 dark:text-white'
                  : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700/50',
              )}
            >
              <span className='flex-shrink-0 mt-0.5'>{option.icon}</span>
              <span className='flex-1 min-w-0'>
                <span className='block text-xs font-medium leading-tight truncate'>
                  {t(option.defaultLabel)}
                </span>
                <span className='block text-[10px] text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2'>
                  {t(option.defaultDescription)}
                </span>
              </span>
            </button>
          ))
        )}
      </div>

      {/* Footer hint */}
      <div className='flex items-center justify-between px-3 py-1.5 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50'>
        <span className='text-[10px] text-gray-500 dark:text-gray-400 flex items-center gap-1'>
          <kbd className='px-1 py-0.5 rounded bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-[9px]'>
            ↑↓
          </kbd>
          {t('navigate')}
        </span>
        <span className='text-[10px] text-gray-500 dark:text-gray-400 flex items-center gap-1'>
          <kbd className='px-1 py-0.5 rounded bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-[9px]'>
            ↵
          </kbd>
          {t('select')}
          <span className='mx-1'>·</span>
          <kbd className='px-1 py-0.5 rounded bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-[9px]'>
            esc
          </kbd>
          {t('close')}
        </span>
      </div>
    </div>
  );
}
